/**
 * Étapes d'apprentissage ou de réapprentissage (design system, TableauDesEtapes ;
 * spécification §3, « Étapes ») : une ligne par passage, une colonne par note. Difficile se
 * saisit avant Bien ; au dernier passage, Bien rend la carte acquise (durée FSRS).
 * La liste n'est enregistrée que complète ; un passage ajouté arrive vide.
 */
import { useEffect, useId, useState } from 'react';
import { validerEtapes, type Etapes } from '../../paquets/reglages';
import { Bouton } from './Bouton';
import { Icone } from './Icone';
import { MessageDeChamp } from './MessageDeChamp';
import { ChampDuree, dureeDepuisMinutes, texteDuree } from './Reglages';

interface Brouillon {
  aRevoir: number | null;
  passages: { difficile: number | null; bien: number | null }[];
}

const ordinal = (n: number) => (n === 1 ? '1er passage' : `${n}e passage`);

function versBrouillon(etapes: Etapes): Brouillon {
  return {
    aRevoir: etapes.aRevoir,
    passages: etapes.passages.map((p) => ({ difficile: p.difficile, bien: p.bien ?? null })),
  };
}

/** Liste enregistrable, ou `null` tant qu'une durée manque. */
function versEtapes(b: Brouillon): Etapes | null {
  if (b.passages.length === 0) return { aRevoir: null, passages: [] };
  if (b.aRevoir === null) return null;
  const passages = [];
  for (const [i, p] of b.passages.entries()) {
    const dernier = i === b.passages.length - 1;
    if (p.difficile === null || (!dernier && p.bien === null)) return null;
    passages.push(dernier ? { difficile: p.difficile } : { difficile: p.difficile, bien: p.bien! });
  }
  const etapes = { aRevoir: b.aRevoir, passages };
  return validerEtapes(etapes).length === 0 ? etapes : null;
}

function Fsrs() {
  return (
    <span
      className="fc-tableau-des-etapes__fsrs"
      title="Durée calculée par FSRS : la carte devient acquise"
    >
      <span aria-hidden="true">FSRS → acquise</span>
      <span className="fc-sr">
        <span>calculée par FSRS, la carte devient acquise</span>
      </span>
    </span>
  );
}

export function TableauDesEtapes({
  liste,
  valeur,
  onChange,
  idTitre,
}: {
  liste: 'apprentissage' | 'reapprentissage';
  valeur: Etapes;
  onChange: (etapes: Etapes) => void;
  idTitre: string;
}) {
  const id = useId();
  const [brouillon, setBrouillon] = useState<Brouillon>(() => versBrouillon(valeur));
  const [focus, setFocus] = useState<string | null>(null);
  useEffect(() => setBrouillon(versBrouillon(valeur)), [valeur]);

  const changer = (b: Brouillon) => {
    setBrouillon(b);
    const etapes = versEtapes(b);
    if (etapes) onChange(etapes);
  };
  const incomplet = versEtapes(brouillon) === null;
  const n = brouillon.passages.length;

  return (
    <div className="fc-tableau-des-etapes" data-liste={liste}>
      <div className="fc-ligne-de-reglage-groupe__panneau fc-tableau-des-etapes__panneau">
        <table
          className="fc-tableau-des-etapes__grille"
          aria-labelledby={idTitre}
          aria-describedby={`${id}-aides`}
        >
          <colgroup>
            <col className="fc-tableau-des-etapes__col-passage" />
            <col />
            <col />
            <col />
            <col />
            <col className="fc-tableau-des-etapes__col-action" />
          </colgroup>
          <thead>
            <tr>
              <td />
              {['1 (À revoir)', '2 (Difficile)', '3 (Bien)', '4 (Facile)'].map((libelle, i) => (
                <th
                  key={libelle}
                  scope="col"
                  className="fc-tableau-des-etapes__entete"
                  id={`${id}-c${i + 1}`}
                >
                  {libelle}
                </th>
              ))}
              <td />
            </tr>
          </thead>
          <tbody>
            {brouillon.passages.map((passage, i) => {
              const dernier = i === n - 1;
              const ligne = `${id}-p${i + 1}`;
              const modifierPassage = (modification: Partial<Brouillon['passages'][number]>) =>
                changer({
                  ...brouillon,
                  passages: brouillon.passages.map((p, j) =>
                    j === i ? { ...p, ...modification } : p,
                  ),
                });
              return (
                <tr key={i} className="fc-tableau-des-etapes__passage">
                  <th scope="row" className="fc-tableau-des-etapes__nom" id={ligne}>
                    {ordinal(i + 1)}
                  </th>
                  <td>
                    {i === 0 ? (
                      <ChampDuree
                        taille="compacte"
                        minutes={brouillon.aRevoir}
                        idsLibelle={[ligne, `${id}-c1`]}
                        erreur={brouillon.aRevoir === null}
                        onChange={(m) => changer({ ...brouillon, aRevoir: m })}
                      />
                    ) : (
                      <span className="fc-tableau-des-etapes__rappel" title="Saisie au 1er passage">
                        {brouillon.aRevoir === null
                          ? '—'
                          : texteDuree(dureeDepuisMinutes(brouillon.aRevoir))}
                        <span className="fc-sr">
                          <span>, saisie au 1er passage</span>
                        </span>
                      </span>
                    )}
                  </td>
                  <td>
                    <ChampDuree
                      taille="compacte"
                      minutes={passage.difficile}
                      idsLibelle={[ligne, `${id}-c2`]}
                      erreur={passage.difficile === null}
                      autoFocus={focus === `${i}-difficile`}
                      onChange={(m) => modifierPassage({ difficile: m })}
                    />
                  </td>
                  <td>
                    {dernier ? (
                      <Fsrs />
                    ) : (
                      <ChampDuree
                        taille="compacte"
                        minutes={passage.bien}
                        idsLibelle={[ligne, `${id}-c3`]}
                        erreur={passage.bien === null}
                        autoFocus={focus === `${i}-bien`}
                        onChange={(m) => modifierPassage({ bien: m })}
                      />
                    )}
                  </td>
                  <td>
                    <Fsrs />
                  </td>
                  <td className="fc-tableau-des-etapes__action">
                    {n > 1 && (
                      <button
                        type="button"
                        className="fc-bouton fc-bouton--discret fc-bouton--petit fc-bouton--icone fc-tableau-des-etapes__supprimer"
                        aria-label={`Supprimer le ${ordinal(i + 1)}`}
                        title={`Supprimer le ${ordinal(i + 1)}`}
                        onClick={() => {
                          const passages = brouillon.passages.filter((_, j) => j !== i);
                          // Le nouveau dernier passage rend la carte acquise par 3 (Bien).
                          if (passages.length > 0)
                            passages[passages.length - 1] = { ...passages.at(-1)!, bien: null };
                          changer({ ...brouillon, passages });
                        }}
                      >
                        <Icone nom="trash-2" />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="fc-tableau-des-etapes__pied">
          <Bouton
            variante="discret"
            taille="compacte"
            icone="plus"
            className="fc-tableau-des-etapes__ajouter"
            onClick={() => {
              setFocus(n === 0 ? '0-difficile' : `${n - 1}-bien`);
              const passages = [...brouillon.passages, { difficile: null, bien: null }];
              setBrouillon({ ...brouillon, passages });
            }}
          >
            Ajouter un passage
          </Bouton>
        </div>
      </div>
      <div className="fc-tableau-des-etapes__aides" id={`${id}-aides`}>
        {incomplet && (
          <MessageDeChamp id={`${id}-erreur`} ton="erreur">
            Saisissez une durée, par exemple 10&nbsp;min. Les étapes sont enregistrées une fois
            toutes les durées saisies.
          </MessageDeChamp>
        )}
        {n === 1 && (
          <MessageDeChamp id={`${id}-aide-1`}>
            conseillé&nbsp;: Difficile ≈ 1,5 × À revoir
          </MessageDeChamp>
        )}
        <MessageDeChamp id={`${id}-aide-2`}>conseillé&nbsp;: Bien ≈ 2 × Difficile</MessageDeChamp>
      </div>
    </div>
  );
}
